import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Facebook, Instagram, Globe, Truck, Search } from "lucide-react";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import NextPage from "../components/NextPage";
import CedarSign, { CedarSignText } from "../components/CedarSign";
import { getOurCowmunity, getOurCowmunityDirectory } from "../lib/api";

const WINK_SRC = "/saltie-wink.png";

/** Renders a paragraph with \n\n as paragraph breaks. */
function Prose({ text, className = "" }) {
  const parts = (text || "").split("\n\n").filter(Boolean);
  return (
    <div className={className}>
      {parts.map((p, i) => (
        <p key={i} className="whitespace-pre-line mb-6 last:mb-0">
          {p}
        </p>
      ))}
    </div>
  );
}

/** A subtle official Saltie Wink butterfly for decorative accent. */
function SaltieWink({ style }) {
  return (
    <img
      src={WINK_SRC}
      alt=""
      aria-hidden="true"
      draggable="false"
      className="pointer-events-none select-none absolute"
      style={{ width: "clamp(3rem, 5vw, 4rem)", opacity: 0.5, ...style }}
    />
  );
}

function DirectoryCard({ card, footerText }) {
  const { farm_name, location, offerings, transporter, website, facebook, instagram } = card;
  return (
    <article
      data-testid={`cowmunity-card-${card.row}`}
      className="relative flex flex-col bg-[#F9F6F0] border border-[#E7DFD5] rounded-md page-shadow overflow-hidden transition-transform duration-500 hover:-translate-y-[2px]"
    >
      <div
        className="absolute top-0 left-0 right-0 h-1.5"
        style={{ background: "#C8A876" }}
      />
      <div className="p-6 pt-8 flex flex-col flex-grow">
        <h3 className="font-display text-2xl text-[#4B3A2F] leading-tight mb-1">
          <span aria-hidden="true">🐮 </span>{farm_name}
        </h3>
        {location && (
          <p className="text-xs uppercase tracking-[0.22em] text-[#D62839] mb-3">
            <span aria-hidden="true">📍 </span>{location}
          </p>
        )}
        {offerings && (
          <p className="font-body text-base text-[#2C2724]/85 leading-relaxed mb-4 whitespace-pre-line">
            {offerings}
          </p>
        )}
        {transporter && (
          <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#7E9268] mb-3">
            <Truck size={12} /> Transporter
          </p>
        )}
        <div className="mt-auto flex flex-wrap gap-2 pt-3">
          {website && (
            <a href={website.startsWith("http") ? website : `https://${website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#A87149] text-[#4B3A2F] text-xs hover:bg-[#A87149] hover:text-white transition-colors">
              <Globe size={12} /> Website
            </a>
          )}
          {facebook && (
            <a href={facebook.startsWith("http") ? facebook : `https://${facebook}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#A87149] text-[#4B3A2F] text-xs hover:bg-[#A87149] hover:text-white transition-colors">
              <Facebook size={12} /> Facebook
            </a>
          )}
          {instagram && (
            <a href={instagram.startsWith("http") ? instagram : `https://${instagram}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#A87149] text-[#4B3A2F] text-xs hover:bg-[#A87149] hover:text-white transition-colors">
              <Instagram size={12} /> Instagram
            </a>
          )}
        </div>
      </div>
      <div className="px-6 pb-4 pt-3 border-t border-[#E7DFD5]/70 flex items-center justify-center gap-2">
        <p className="font-script text-base text-[#6D5A50] text-center">
          {footerText}
        </p>
        <img src={WINK_SRC} alt="" aria-hidden="true" className="w-4 h-4 opacity-80 select-none" draggable="false" />
      </div>
    </article>
  );
}

export default function OurCowmunity() {
  const [c, setC] = useState(null);
  const [dir, setDir] = useState(null);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getOurCowmunity().then(setC).catch(() => setC({}));
    getOurCowmunityDirectory().then(setDir).catch(() => setDir({ cards: [], count: 0 }));
  }, []);

  // Full result of the current search (or all cards if no search).
  const searchResults = useMemo(() => {
    if (!dir?.cards) return [];
    if (!q.trim()) return dir.cards;
    const needle = q.trim().toLowerCase();
    return dir.cards.filter((r) =>
      [r.farm_name, r.location, r.offerings].join(" ").toLowerCase().includes(needle),
    );
  }, [dir, q]);

  const searching = q.trim().length > 0;
  // Default view: show first 12 unless the visitor has expanded
  // "Explore the Entire Salt Trail" OR is actively searching.
  const visible = searching || showAll ? searchResults : searchResults.slice(0, 12);
  const truncated = !searching && !showAll && searchResults.length > 12;

  if (!c) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center">
        <p className="text-sm text-[#6D5A50] italic">{"loading\u2026"}</p>
      </div>
    );
  }

  return (
    <div className="parchment min-h-screen" data-testid="our-cow-munity-page">
      <SEO
        title={c.seo_title || "Our Cow-munity"}
        description={c.seo_description || "The herd behind the herd — the Cow-munity that helped shape The Salty Moo. Meet the farms, ranches, rescues, and good folks along The Salt Trail."}
        path="/our-cow-munity"
      />

      <ChapterHero
        path="/our-cow-munity"
        image="/assets/heroes/our-cow-munity.webp"
        title={c.hero_title || "Our Cow-munity"}
        subtitle={c.hero_subtitle || "The herd behind the herd\u2014the folks who helped shape this dream."}
        position="top-left"
      />

      <div className="max-w-5xl mx-auto px-6 -mt-8 relative z-10 pb-28">

        {/* ================================================================
             SECTION 1 — The Herd Behind the Herd (intro + thank-you)
             ================================================================ */}
        <section data-testid="cowmunity-intro" className="relative pt-16">
          <SaltieWink style={{ top: "-1rem", right: "4%", transform: "rotate(14deg) scaleX(-1)" }} />

          <div className="flex justify-center">
            <CedarSign size="lg">
              <CedarSignText size="text-2xl sm:text-3xl md:text-5xl">
                <span className="block text-center leading-tight">
                  {c.page_title}
                </span>
              </CedarSignText>
            </CedarSign>
          </div>

          <div className="mt-14 max-w-3xl mx-auto text-center">
            <Prose
              text={c.intro_body}
              className="font-body text-xl md:text-2xl leading-loose text-[#2C2724]/90"
            />
          </div>
        </section>

        {/* ================================================================
             SECTION 2 — The Salt Trail (directory)
             ================================================================ */}
        {c.show_salt_trail !== false && (
          <section data-testid="cowmunity-salt-trail" className="relative mt-28">
            <SaltieWink style={{ top: "-1rem", left: "5%" }} />

            <div className="flex justify-center">
              <CedarSign size="lg">
                <CedarSignText size="text-3xl md:text-5xl">
                  <span className="block text-center leading-tight px-2 py-1">
                    {(c.salt_trail_heading || "Who the Moo Are You?").toUpperCase()}
                  </span>
                </CedarSignText>
              </CedarSign>
            </div>

            <p className="font-script text-center text-4xl md:text-5xl text-[#4B3A2F] mt-10">
              {c.salt_trail_tagline}
            </p>

            <div className="mt-10 max-w-3xl mx-auto">
              <Prose
                text={c.salt_trail_body}
                className="font-body text-xl md:text-2xl leading-loose text-[#2C2724]/90"
              />
            </div>

            {/* "Who the Moo Are You?" is the button only — no separate heading */}
            <div className="mt-14 flex justify-center">
              <a
                href={c.form_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cowmunity-form-btn"
                className="btn-pasture inline-flex items-center gap-2 text-lg"
                style={{ padding: "0.9rem 2rem 1rem" }}
              >
                <ExternalLink size={18} />
                {c.button_text}
              </a>
            </div>

            {/* Browse the trail */}
            <div className="mt-20 text-center">
              <h3 className="font-display text-3xl md:text-4xl text-[#4B3A2F] mb-2">
                {c.browse_heading || "Browse The Salt Trail"}
              </h3>
              <p className="font-script text-2xl text-[#4B3A2F]">
                {c.browse_tagline || "every stop is a story"}
              </p>
            </div>

            {/* Directory search */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6D5A50] pointer-events-none"
                />
                <input
                  data-testid="cowmunity-search"
                  type="search"
                  placeholder={c.search_placeholder || "Search by farm, state, or offerings\u2026"}
                  className="w-full pl-12 pr-4 py-4 rounded-md bg-[#F9F6F0] border border-[#E7DFD5] focus:outline-none focus:border-[#4B3A2F] font-body text-base text-[#2C2724]"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              {dir && (
                <p className="text-xs uppercase tracking-[0.28em] text-[#6D5A50] mt-4 text-center">
                  {searching
                    ? `${searchResults.length} of ${dir.count || 0} match your search`
                    : showAll
                    ? `${searchResults.length} along the trail`
                    : `showing 12 of ${dir.count || 0} along the trail`}
                </p>
              )}
            </div>

            {/* Directory cards */}
            <div
              data-testid="cowmunity-directory"
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {visible.map((card) => (
                <DirectoryCard key={card.row} card={card} footerText={c.card_footer} />
              ))}
            </div>

            {/* Explore the Entire Salt Trail — only in default 12-card view */}
            {truncated && (
              <div className="mt-14 flex justify-center">
                <button
                  type="button"
                  data-testid="cowmunity-explore-all"
                  onClick={() => setShowAll(true)}
                  className="btn-pasture inline-flex items-center gap-2 text-lg"
                  style={{ padding: "0.85rem 1.9rem 0.95rem" }}
                >
                  Explore the Entire Salt Trail
                </button>
              </div>
            )}

            {dir && dir.count === 0 && (
              <p className="text-center italic text-[#6D5A50] mt-10">
                {c.empty_trail_message || "The trail is just beginning. Check back soon."}
              </p>
            )}
            {searching && searchResults.length === 0 && (
              <p className="text-center italic text-[#6D5A50] mt-10">
                {c.no_matches_message || "No matches yet \u2014 try another farm, state, or word."}
              </p>
            )}
          </section>
        )}
      </div>

      <NextPage to="/moo-velous" label={c.cowmunity_next_button_label || "Caught Something Moo-velous?"} />
    </div>
  );
}
